/**
 * HYPRISM THEME - Core JavaScript
 * =============================================================================
 * Handles: Dark/Light Mode Toggle, Flashlight Effect, Scroll Animations
 * =============================================================================
 */

/* =============================================================================
   1. DARK / LIGHT MODE TOGGLE
   =============================================================================
   This is the unified dark/light mode system.
   It works together with the dark-light-toggle.liquid snippet and
   css-variables.liquid to swap color schemes on sections.

   State: localStorage 'ne-color-mode' = 'primary' | 'alternate'
   Attribute: <html data-ne-mode="primary|alternate">

   The toggle reads counterpart mappings from a global JS object
   (window.__NE_SCHEME_PAIRS__) injected by dark-light-toggle.liquid,
   and swaps [data-color-scheme] attributes on sections accordingly.
   ============================================================================= */

class NEDarkLightToggle extends HTMLElement {
  connectedCallback() {
    this.button = this.querySelector('button');
    if (!this.button) return;

    this.updateIcon();

    this.button.addEventListener('click', () => {
      const current = document.documentElement.getAttribute('data-ne-mode') || 'primary';
      const next = current === 'primary' ? 'alternate' : 'primary';

      document.documentElement.setAttribute('data-ne-mode', next);
      localStorage.setItem('ne-color-mode', next);

      this.swapSchemes(next);
      this.updateIcon();

      // Sync all toggle instances on the page
      document.querySelectorAll('ne-dark-light-toggle').forEach(el => {
        if (el !== this) el.updateIcon();
      });
    });
  }

  swapSchemes(mode) {
    const pairs = window.__NE_SCHEME_PAIRS__ || {};
    const sections = document.querySelectorAll('[data-color-scheme]');

    sections.forEach(section => {
      const currentScheme = section.getAttribute('data-color-scheme');
      const originalScheme = section.getAttribute('data-color-scheme-original') || currentScheme;

      // Store the original scheme on first toggle
      if (!section.hasAttribute('data-color-scheme-original')) {
        section.setAttribute('data-color-scheme-original', currentScheme);
      }

      if (mode === 'alternate') {
        // Switch to alternate: look up the counterpart
        const alternate = pairs[originalScheme];
        if (alternate) {
          section.setAttribute('data-color-scheme', alternate);
        }
      } else {
        // Switch back to primary: restore original
        section.setAttribute('data-color-scheme', originalScheme);
      }
    });
  }

  updateIcon() {
    const mode = document.documentElement.getAttribute('data-ne-mode') || 'primary';
    const iconSun = this.querySelector('.ne-toggle-icon--sun');
    const iconMoon = this.querySelector('.ne-toggle-icon--moon');
    if (iconSun && iconMoon) {
      // In primary (dark) mode, show sun icon (to switch to light)
      // In alternate (light) mode, show moon icon (to switch to dark)
      iconSun.style.display = mode === 'primary' ? 'block' : 'none';
      iconMoon.style.display = mode === 'alternate' ? 'block' : 'none';
    }
    if (this.button) {
      this.button.setAttribute('aria-label',
        mode === 'primary' ? 'Switch to light mode' : 'Switch to dark mode'
      );
    }
  }
}

if (!customElements.get('ne-dark-light-toggle')) {
  customElements.define('ne-dark-light-toggle', NEDarkLightToggle);
}

/* =============================================================================
   2. X-RAY EFFECT
   ============================================================================= */

class XRayEffect {
  constructor() {
    this.layer = document.querySelector('.ne-layer-xray');
    if (!this.layer) return;

    this.enabled = this.layer.dataset.enabled === 'true';
    if (!this.enabled) return;

    // Check for reduced motion preference
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    // Set vars on parent background container too, so the overlay's mask-image
    // can access them (CSS vars only cascade to children, not siblings)
    this.bg = this.layer.closest('.ne-layer-background') || this.layer;

    // Don't add --active on load; only activate on first real mousemove inside the viewport
    document.addEventListener('mousemove', (e) => {
      this.layer.classList.add('ne-layer-xray--active');
      requestAnimationFrame(() => {
        const x = e.clientX + 'px';
        const y = e.clientY + 'px';
        this.layer.style.setProperty('--ne-mouse-x', x);
        this.layer.style.setProperty('--ne-mouse-y', y);
        this.bg.style.setProperty('--ne-mouse-x', x);
        this.bg.style.setProperty('--ne-mouse-y', y);
      });
    }, { passive: true });

    // Hide immediately when mouse leaves the browser window
    document.documentElement.addEventListener('mouseleave', () => {
      this.layer.classList.remove('ne-layer-xray--active');
    });

    // Hide on touch devices
    document.addEventListener('touchstart', () => {
      this.layer.classList.remove('ne-layer-xray--active');
    }, { once: true, passive: true });
  }
}

/* =============================================================================
   3. SCROLL ANIMATIONS (Intersection Observer)
   ============================================================================= */

class ScrollAnimations {
  constructor() {
    // Feature is controlled by [data-ne-animations] on <html>, set by inline script in theme.liquid
    // before first paint. If the attribute is absent, animations are disabled or style is "none".
    if (!document.documentElement.hasAttribute('data-ne-animations')) return;

    // Respect reduced motion — mark everything visible immediately
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      document.querySelectorAll('.ne-section, .ne-block-adv--anim').forEach(el => {
        el.classList.add('ne-animate--visible');
      });
      return;
    }

    this.replayMode = document.documentElement.getAttribute('data-ne-animations-replay') || 'once';

    this.observer = new IntersectionObserver(
      (entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            const delay = parseInt(entry.target.dataset.animateDelay || 0, 10);
            setTimeout(() => {
              entry.target.classList.add('ne-animate--visible');
            }, delay);
            if (this.replayMode !== 'always') {
              this.observer.unobserve(entry.target);
            }
          } else if (this.replayMode === 'always') {
            // Reset so the keyframe animation can play again on next entry
            entry.target.classList.remove('ne-animate--visible');
          }
        });
      },
      {
        threshold: 0.05,
        rootMargin: '0px 0px -40px 0px'
      }
    );

    this._initSections(document);
  }

  _initSections(scope) {
    // Two-pass init: first RAF commits opacity:0/transform to the screen;
    // second RAF triggers the transition so in-viewport sections animate in.
    // Without the double-RAF, the browser never paints the initial hidden state
    // and skips the transition entirely (jumps straight to visible).
    requestAnimationFrame(() => {
      const vh = window.innerHeight;
      const inView = [];

      scope.querySelectorAll('.ne-section:not(.ne-animate--visible), .ne-block-adv--anim:not(.ne-animate--visible)').forEach(el => {
        const rect = el.getBoundingClientRect();
        if (rect.top < vh && rect.bottom > 0) {
          inView.push(el);
        } else {
          // Below the fold — observe for scroll-triggered animation
          this.observer.observe(el);
        }
      });

      // Second RAF: initial hidden state is now committed, transition can play
      requestAnimationFrame(() => {
        inView.forEach(el => {
          el.classList.add('ne-animate--visible');
          // Replay mode: keep observing so we can reset + retrigger on re-enter
          if (this.replayMode === 'always') {
            this.observer.observe(el);
          }
        });
      });
    });
  }
}

/* =============================================================================
   4. MARQUEE PAUSE ON HOVER
   ============================================================================= */

class MarqueeController {
  constructor() {
    document.querySelectorAll('.ne-marquee').forEach(marquee => {
      const track = marquee.querySelector('.ne-marquee__track');
      if (!track) return;

      marquee.addEventListener('mouseenter', () => {
        track.style.animationPlayState = 'paused';
      });

      marquee.addEventListener('mouseleave', () => {
        track.style.animationPlayState = 'running';
      });
    });
  }
}

/* =============================================================================
   5. SMOOTH SCROLL FOR ANCHOR LINKS
   ============================================================================= */

class SmoothScroll {
  constructor() {
    document.querySelectorAll('a[href^="#"]').forEach(anchor => {
      anchor.addEventListener('click', (e) => {
        const targetId = anchor.getAttribute('href');
        if (targetId === '#') return;

        const target = document.querySelector(targetId);
        if (!target) return;

        e.preventDefault();
        target.scrollIntoView({
          behavior: 'smooth',
          block: 'start'
        });

        // Update focus for accessibility
        target.setAttribute('tabindex', '-1');
        target.focus({ preventScroll: true });
      });
    });
  }
}

/* =============================================================================
   6. INITIALIZATION
   ============================================================================= */

// Early mode restoration (prevent flash of wrong mode)
(function() {
  var saved = localStorage.getItem('ne-color-mode');
  if (saved) {
    document.documentElement.setAttribute('data-ne-mode', saved);
  }
})();

// Initialize everything else when DOM is ready
document.addEventListener('DOMContentLoaded', () => {
  new XRayEffect();
  const scrollAnim = new ScrollAnimations();
  new MarqueeController();
  new SmoothScroll();

  // Re-init scroll animations when a section is reloaded in the Customizer
  document.addEventListener('shopify:section:load', (e) => {
    if (scrollAnim && scrollAnim.observer) {
      scrollAnim._initSections(e.target);
    }
  });

  // Apply saved mode to sections on page load
  var saved = localStorage.getItem('ne-color-mode');
  if (saved === 'alternate') {
    var pairs = window.__NE_SCHEME_PAIRS__ || {};
    document.querySelectorAll('[data-color-scheme]').forEach(function(section) {
      var scheme = section.getAttribute('data-color-scheme');
      section.setAttribute('data-color-scheme-original', scheme);
      var alternate = pairs[scheme];
      if (alternate) {
        section.setAttribute('data-color-scheme', alternate);
      }
    });
  }
});
